// StaffFinalizationView.jsx
import React, { useState, useEffect } from 'react';
import { Button, TextAreaField } from '@dhis2/ui';
import { useRatingScaleService, DEFAULT_RATING_SCALE } from '../api/RatingScaleService';

const StaffFinalizationView = ({ 
  competency, 
  index, 
  period,
  onSave,
  isSubmitting,
  isReadOnly,
  isFinalized 
}) => {
  // SAFE: Ensure arrays exist with fallbacks
  const staffAgreement = competency.staffAgreement || [null, null];
  const staffAgreementComment = competency.staffAgreementComment || ["", ""];
  const managerRatings = competency.managerRatings || [null, null];
  const managerComments = competency.managerComments || ["", ""];
  const ratings = competency.ratings || [null, null];
  const comments = competency.comments || ["", ""];

  const { loadRatingScale, getRatingLabel, getRatingColor } = useRatingScaleService();
  const [ratingScale, setRatingScale] = useState(DEFAULT_RATING_SCALE);

  useEffect(() => {
    loadRatingScale().then(setRatingScale);
  }, []);

  // Use 'yes' or 'no' instead of boolean
  const [agreement, setAgreement] = useState(() => {
    const value = staffAgreement[period];
    if (value === true) return 'yes';
    if (value === false) return 'no';
    return null; // Not selected yet
  });
  const [disagreementComment, setDisagreementComment] = useState(
    staffAgreementComment[period] || ''
  );
  const [isSaving, setIsSaving] = useState(false);

  // Update state when props change (e.g., when unfinalized)
  useEffect(() => {
    const value = staffAgreement[period];
    if (value === true) setAgreement('yes');
    else if (value === false) setAgreement('no');
    else setAgreement(null);
    setDisagreementComment(staffAgreementComment[period] || '');
  }, [staffAgreement, staffAgreementComment, period]);

  const handleSave = async () => {
    if (!agreement) {
      alert('Please select Yes or No to indicate if you agree with the manager\'s assessment');
      return;
    }
    
    if (agreement === 'no' && !disagreementComment.trim()) {
      alert('Please provide an explanation for why you disagree');
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave(index, period, {
        agrees: agreement === 'yes', // Convert to boolean for the save function
        comment: agreement === 'no' ? disagreementComment : ''
      });
    } finally {
      setIsSaving(false);
    }
  };

  const hasManagerRating = managerRatings[period] !== null && managerRatings[period] !== undefined;
  
  // Determine if the button should be enabled
  const isButtonEnabled = () => {
    if (isFinalized) return false; // Already finalized
    if (!hasManagerRating) return false; // No manager rating yet
    if (!agreement) return false; // No selection made
    if (agreement === 'no' && !disagreementComment.trim()) return false; // Disagreed but no comment
    return true;
  };

  // Get button text based on state
  const getButtonText = () => {
    if (isSaving) return 'Saving...';
    if (isFinalized) return '✓ Finalized';
    return 'Finalize & Submit';
  };

  // Get button style based on state
  const getButtonStyle = () => {
    if (isFinalized) {
      return {
        marginTop: '8px',
        width: '100%',
        backgroundColor: '#4caf50',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      };
    }
    if (isButtonEnabled()) {
      return {
        marginTop: '8px',
        width: '100%',
        backgroundColor: '#4caf50',
        color: 'white'
      };
    }
    return {
      marginTop: '8px',
      width: '100%',
      backgroundColor: '#cccccc',
      color: 'white'
    };
  };

  if (!hasManagerRating) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#fff3e0',
        borderRadius: '8px',
        textAlign: 'center',
        color: '#ff9800'
      }}>
        ⏳ Waiting for manager's review...
      </div>
    );
  }

  if (isFinalized) {
    const agreed = staffAgreement[period] === true;
    return (
      <div style={{
        padding: '16px',
        border: '1px solid #4caf50',
        borderRadius: '8px',
        marginBottom: '16px',
        backgroundColor: '#e8f5e9'
      }}>
        <div style={{ marginBottom: '12px' }}>
          <strong style={{ fontSize: '1rem', color: '#2c6693' }}>{competency.name}</strong>
          <div 
            className="rich-text-content" 
            style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}
          >
            <div dangerouslySetInnerHTML={{ __html: competency.definition || 'No description available' }} />
          </div>
        </div>

        {/* Your Self-Assessment */}
        <div style={{
          padding: '12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '6px',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px', color: '#2c6693' }}>
            Your Self-Assessment:
          </div>
          <div>Rating: <strong>{ratings[period] ? getRatingLabel(ratings[period]) : 'Not provided'}</strong></div>
          <div>Comment: <em>{comments[period] || 'No comment'}</em></div>
        </div>

        {/* Manager's Assessment */}
        <div style={{
          padding: '12px',
          backgroundColor: '#fff8e1',
          borderRadius: '6px',
          marginBottom: '16px',
          borderLeft: '3px solid #ff9800'
        }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px', color: '#e65100' }}>
            Manager's Assessment:
          </div>
          <div>Rating: <strong>{managerRatings[period]} / 5</strong></div>
          <div style={{ marginTop: '4px' }}>
            Feedback: <em>{managerComments[period]}</em>
          </div>
        </div>

        {/* Finalized Status Display */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#e8f5e9',
          borderRadius: '8px',
          border: '1px solid #4caf50'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              backgroundColor: '#4caf50',
              color: 'white',
              borderRadius: '20px',
              padding: '4px 12px',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>✓</span>
              <span>FINALIZED</span>
            </div>
            <span style={{ fontSize: '0.85rem', color: '#2e7d32' }}>
              {agreed ? 'You agreed with the manager\'s assessment' : 'You disagreed with the manager\'s assessment'}
            </span>
          </div>
          
          {/* ✓ Submitted Button */}
          <Button
            primary
            disabled={true}
            style={{
              backgroundColor: '#4caf50',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'default',
              opacity: 0.8
            }}
          >
            <span>✓</span>
            <span>Submitted</span>
          </Button>
        </div>
        
        {/* Show feedback if disagreed */}
        {!agreed && staffAgreementComment[period] && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: '#fff3e0',
            borderRadius: '6px',
            fontSize: '0.85rem',
            color: '#e65100',
            borderLeft: '3px solid #ff9800'
          }}>
            <strong>Your feedback:</strong> "{staffAgreementComment[period]}"
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      padding: '16px',
      border: '1px solid #2196f3',
      borderRadius: '8px',
      marginBottom: '16px',
      backgroundColor: '#e3f2fd'
    }}>
      <div style={{ marginBottom: '12px' }}>
        <strong style={{ fontSize: '1rem' }}>{competency.name}</strong>
        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
          <div dangerouslySetInnerHTML={{ __html: competency.definition }} />
        </p>
      </div>

      {/* Your Self-Assessment */}
      <div style={{
        padding: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '6px',
        marginBottom: '16px'
      }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px', color: '#2c6693' }}>
          Your Self-Assessment:
        </div>
        <div>Rating: <strong>{ratings[period] || 'Not provided'}</strong></div>
        <div>Comment: <em>{comments[period] || 'No comment'}</em></div>
      </div>

      {/* Manager's Assessment */}
      <div style={{
        padding: '12px',
        backgroundColor: '#fff8e1',
        borderRadius: '6px',
        marginBottom: '16px',
        borderLeft: '3px solid #ff9800'
      }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px', color: '#e65100' }}>
          Manager's Assessment:
        </div>
        <div>Rating: <strong>{managerRatings[period]} / 5</strong></div>
        <div style={{ marginTop: '4px' }}>
          Feedback: <em>{managerComments[period]}</em>
        </div>
      </div>

      {/* Staff Agreement Section - Yes/No Radio Buttons */}
      <div style={{ marginTop: '16px' }}>
        <label style={{ 
          fontWeight: 600, 
          marginBottom: '12px', 
          display: 'block',
          color: '#2c6693'
        }}>
          Do you agree with the manager's assessment? <span style={{ color: 'red' }}>*</span>
        </label>
        
        <div style={{ 
          display: 'flex', 
          gap: '24px', 
          marginBottom: '16px',
          flexWrap: 'wrap'
        }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: isSubmitting || isReadOnly ? 'not-allowed' : 'pointer',
            opacity: isSubmitting || isReadOnly ? 0.6 : 1
          }}>
            <input
              type="radio"
              name={`agreement-${competency.competencyId}-${period}`}
              value="yes"
              checked={agreement === 'yes'}
              onChange={() => setAgreement('yes')}
              disabled={isSubmitting || isReadOnly || isFinalized}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ fontSize: '1rem', color: '#28a745' }}>✅ Yes, I agree</span>
          </label>
          
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: isSubmitting || isReadOnly ? 'not-allowed' : 'pointer',
            opacity: isSubmitting || isReadOnly ? 0.6 : 1
          }}>
            <input
              type="radio"
              name={`agreement-${competency.competencyId}-${period}`}
              value="no"
              checked={agreement === 'no'}
              onChange={() => setAgreement('no')}
              disabled={isSubmitting || isReadOnly || isFinalized}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ fontSize: '1rem', color: '#dc3545' }}>❌ No, I disagree</span>
          </label>
        </div>

        {/* Show comment box only if user selects "No" */}
        {agreement === 'no' && (
          <div style={{ marginBottom: '16px' }}>
            <TextAreaField
              value={disagreementComment}
              onChange={({ value }) => setDisagreementComment(value)}
              placeholder="Please explain why you disagree with the manager's assessment..."
              label="Your feedback / Disagreement Explanation"
              rows={3}
              disabled={isSubmitting || isReadOnly || isFinalized}
            />
          </div>
        )}

        {/* Selected agreement status message */}
        {agreement && (
          <div style={{
            marginTop: '12px',
            marginBottom: '16px',
            padding: '8px 12px',
            backgroundColor: agreement === 'yes' ? '#e8f5e9' : '#fff3e0',
            borderRadius: '4px',
            fontSize: '0.85rem',
            color: agreement === 'yes' ? '#2e7d32' : '#e65100',
            borderLeft: `3px solid ${agreement === 'yes' ? '#4caf50' : '#ff9800'}`
          }}>
            {agreement === 'yes' 
              ? '✓ I confirm that we had a discussion on my behavioural assessment.' 
              : '⚠️ I confirm that we had a discussion on my behavioural assessment.'}
          </div>
        )}

        {/* Finalize & Submit Button */}
        <Button
          primary
          onClick={handleSave}
          disabled={!isButtonEnabled() || isSaving || isSubmitting}
          style={getButtonStyle()}
        >
          {getButtonText()}
        </Button>
        
        {/* Button status helper text - Only show when not finalized and button disabled */}
        {!isButtonEnabled() && hasManagerRating && !isFinalized && (
          <div style={{
            marginTop: '8px',
            padding: '6px 12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            fontSize: '0.75rem',
            color: '#666',
            textAlign: 'center'
          }}>
            {!agreement ? '📌 Please select Yes or No above to enable finalization' : 
             agreement === 'no' && !disagreementComment.trim() ? '📝 Please provide an explanation for your disagreement' : 
             '✅ All requirements met - ready to finalize'}
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffFinalizationView;